import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * GraphQL codegen types `URL` fields as the DOM `URL` global, but at runtime
 * they're plain strings. Accept either at the boundary and normalize internally.
 */
type UrlLike = string | URL | null | undefined;

export interface CachePageInfo {
  /** Path on the public site, e.g. /articles/123-headline */
  canonicalPath?: UrlLike;
  /** Optional fully-qualified URL. Overrides siteUrlBase + canonicalPath when provided. */
  canonicalUrl?: UrlLike;
  /** Human label for the dropdown, e.g. "this article", "this page" */
  label?: string;
}

export interface NormalizedCachePageInfo {
  canonicalPath?: string;
  canonicalUrl?: string;
  label?: string;
}

interface CachePageContextValue {
  current: NormalizedCachePageInfo | null;
  setCurrent: (info: NormalizedCachePageInfo | null) => void;
}

function toStringOrUndefined(value: UrlLike): string | undefined {
  if (value === null || value === undefined) return undefined;
  return typeof value === 'string' ? value : String(value);
}

const CachePageContext = createContext<CachePageContextValue>({
  current: null,
  setCurrent: () => {},
});

export const CachePageProvider = ({ children }: { children: React.ReactNode }) => {
  const [current, setCurrent] = useState<NormalizedCachePageInfo | null>(null);

  const value = useMemo(
    () => ({ current, setCurrent }),
    [current],
  );

  return <CachePageContext.Provider value={value}>{children}</CachePageContext.Provider>;
};

export function useCachePage(): CachePageContextValue {
  return useContext(CachePageContext);
}

/**
 * Editors call this hook to publish the public-facing URL of the entity being edited.
 * The header dropdown reads this so its "Clear Current Page" option targets the
 * exact path. Pass null/undefined values to clear the registration on unmount or
 * before the entity has loaded.
 */
export function useRegisterCachePage(info: CachePageInfo | null | undefined): void {
  const { setCurrent } = useCachePage();
  const canonicalPath = toStringOrUndefined(info?.canonicalPath);
  const canonicalUrl = toStringOrUndefined(info?.canonicalUrl);
  const label = info?.label;

  const stable = useCallback((): NormalizedCachePageInfo | null => {
    if (!canonicalPath && !canonicalUrl) return null;
    return { canonicalPath, canonicalUrl, label };
  }, [canonicalPath, canonicalUrl, label]);

  useEffect(() => {
    setCurrent(stable());
    return () => setCurrent(null);
  }, [setCurrent, stable]);
}
